import type {
  CatalogDraftDocument,
  DraftCheck,
  DraftMetricOverride,
  PlaygroundCatalogMetric,
} from "contracts/api/playground/draft";

/** Editable copy of one metric; diffed against canonical on save so the
 * draft document carries only what actually changed. */
export type EditableMetric = Pick<
  PlaygroundCatalogMetric,
  "key" | "label" | "pillar" | "description" | "thresholds"
> & {
  checks: DraftCheck[];
  subcriteria: {
    key: string;
    label: string;
    definition: string;
    evidenceGuidance: string;
  }[];
};

export function toEditable(
  canonical: PlaygroundCatalogMetric[],
  document: CatalogDraftDocument,
): EditableMetric[] {
  return canonical.map((metric) => {
    const override = document.metrics[metric.key];
    return {
      key: metric.key,
      label: metric.label,
      pillar: metric.pillar,
      description: override?.description ?? metric.description,
      thresholds: { ...metric.thresholds, ...override?.thresholds },
      checks: (override?.checks ?? metric.checks).map((check) => ({
        ...check,
      })),
      subcriteria: metric.subcriteria.map((sub) => ({
        key: sub.key,
        label: sub.label,
        definition:
          override?.subcriteria?.[sub.key]?.definition ?? sub.definition,
        evidenceGuidance:
          override?.subcriteria?.[sub.key]?.evidenceGuidance ??
          sub.evidenceGuidance,
      })),
    };
  });
}

export function diffDocument(
  canonical: PlaygroundCatalogMetric[],
  edited: EditableMetric[],
): CatalogDraftDocument {
  const document: CatalogDraftDocument = { metrics: {} };
  for (const metric of edited) {
    const base = canonical.find((row) => row.key === metric.key);
    if (!base) continue;
    const override: DraftMetricOverride = {};
    if (metric.description !== base.description) {
      override.description = metric.description;
    }
    if (
      metric.thresholds.target !== base.thresholds.target ||
      metric.thresholds.good !== base.thresholds.good ||
      metric.thresholds.watch !== base.thresholds.watch
    ) {
      override.thresholds = { ...metric.thresholds };
    }
    if (JSON.stringify(metric.checks) !== JSON.stringify(base.checks)) {
      override.checks = metric.checks;
    }
    const subPatches: NonNullable<DraftMetricOverride["subcriteria"]> = {};
    for (const sub of metric.subcriteria) {
      const baseSub = base.subcriteria.find((row) => row.key === sub.key);
      if (!baseSub) continue;
      const patch: Record<string, string> = {};
      if (sub.definition !== baseSub.definition) {
        patch.definition = sub.definition;
      }
      if (sub.evidenceGuidance !== baseSub.evidenceGuidance) {
        patch.evidenceGuidance = sub.evidenceGuidance;
      }
      if (Object.keys(patch).length > 0) subPatches[sub.key] = patch;
    }
    if (Object.keys(subPatches).length > 0) {
      override.subcriteria = subPatches;
    }
    if (Object.keys(override).length > 0) {
      document.metrics[metric.key] = override;
    }
  }
  return document;
}
